import { PartyPopper, Undo2, X } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { type GetThemeValueForKey, View, XStack, YStack } from "tamagui";
import type PlanModel from "../../../plans/PlanModel";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Checkbox } from "../../../ui/Checkbox";
import { Label } from "../../../ui/Label";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import { Separator } from "../../../ui/Separator";
import { Subtext } from "../../../ui/Subtext";
import { TipsText } from "../../../ui/TipsText";
import { AvatarSize, UserPropsAvatar } from "../../../ui/UserAvatar";
import { UserAvatarGroupRaw } from "../../../ui/UserAvatarGroupRaw";
import { BusinessModuleMembersSheet } from "../../components/BusinessModuleMembersSheet";
import type { BusinessStaticUserInfoWithUserUid } from "../../plan/BusinessPlanModel";
import type { BusinessGenericModel } from "../types";

export const UserDisplay: React.FC<
  { user: BusinessStaticUserInfoWithUserUid } & {
    gap?: GetThemeValueForKey<"size">;
    subtext?: string | JSX.Element | undefined;
  }
> = observer(({ gap, subtext, user }) => {
  return (
    <XStack alignItems="center" gap={gap ?? "$medium"}>
      <UserPropsAvatar {...user} />
      <View>
        <Body>{user?.displayName ?? "Unknown"}</Body>
        {subtext && typeof subtext === "string" ? (
          <Subtext>{subtext}</Subtext>
        ) : (
          subtext
        )}
      </View>
    </XStack>
  );
});

const UserRow: React.FC<{
  businessModule: BusinessGenericModel;
  user: BusinessStaticUserInfoWithUserUid;
}> = ({ user, businessModule }) => {
  const { t } = useTranslation();
  const excluded = businessModule.data.excludedUsers.includes(user.userUid);

  return (
    <XStack
      key={user.userUid}
      opacity={excluded ? 0.5 : 1}
      width={"100%"}
      paddingHorizontal={"$larger"}
      paddingVertical={"$medium"}
      alignItems="center"
      justifyContent="space-between"
      onPress={() => businessModule.toggleCompletedByUserId(user.userUid)}
      borderTopColor={"$divider"}
      borderTopWidth={1}
    >
      <UserDisplay user={user} />
      <XStack alignItems="center" gap="$large">
        {businessModule.data.completedBy.some(
          (d) => d.userUid === user.userUid,
        ) ? undefined : (
          <Subtext
            cursor="pointer"
            onPress={(e) => {
              e.stopPropagation();
              businessModule.toggleExcluded(user.userUid);
            }}
            textDecorationLine="underline"
          >
            {excluded ? t("business:include_user") : t("business:exclude_user")}
          </Subtext>
        )}
        {!excluded && (
          <Checkbox
            checked={businessModule.data.completedBy.some(
              (d) => d.userUid === user.userUid,
            )}
          />
        )}
      </XStack>
    </XStack>
  );
};

const PlanUsersCompletedList = observer(
  ({
    businessModule,
    plan,
  }: {
    businessModule: BusinessGenericModel;
    plan: PlanModel;
  }) => {
    const users = plan.businessStaticUsers;
    const { t } = useTranslation();
    const usersNotExcluded = users.filter(
      (user: BusinessStaticUserInfoWithUserUid) =>
        !businessModule.data.excludedUsers.includes(user.userUid),
    );

    const completedCount = usersNotExcluded.filter(
      (user: BusinessStaticUserInfoWithUserUid) =>
        businessModule.data.completedBy.some((d) => d.userUid === user.userUid),
    ).length;

    const completedByEveryone = usersNotExcluded.length === completedCount;

    const toggleEveryoneCompleted = React.useCallback(() => {
      businessModule.toggleCompletedByUserIds(
        usersNotExcluded.map((user) => user.userUid),
        !completedByEveryone,
      );
    }, [usersNotExcluded, completedByEveryone, businessModule]);

    return (
      <Box noPadding borderTopWidth={1} borderTopColor={"$divider"}>
        <View>
          <XStack
            width={"100%"}
            paddingHorizontal={"$larger"}
            paddingVertical={"$medium"}
            alignItems="center"
            justifyContent="space-between"
            onPress={toggleEveryoneCompleted}
            cursor="pointer"
          >
            <Label>
              {completedByEveryone && t("business:completed_by_everyone")}
              {!completedByEveryone &&
                t("business:completed_by_count") +
                  `(${completedCount}/${usersNotExcluded.length})`}
            </Label>
            <Checkbox
              checked={completedCount > 0}
              semiChecked={!completedByEveryone && completedCount > 0}
            />
          </XStack>
          {users
            .sort((a, b) => {
              const aExcluded = businessModule.data.excludedUsers.includes(
                a.userUid,
              );
              const bExcluded = businessModule.data.excludedUsers.includes(
                b.userUid,
              );
              if (aExcluded !== bExcluded) {
                return aExcluded ? 1 : -1;
              }

              return a.displayName.localeCompare(b.displayName);
            })
            .map((user) => (
              <UserRow
                key={user.userUid}
                user={user}
                businessModule={businessModule}
              />
            ))}
        </View>
      </Box>
    );
  },
);

const TotalCompletedInfo = observer(
  ({ businessModule }: { businessModule: BusinessGenericModel }) => {
    const feedInfo = businessModule.feedInfo;

    const [showSheet, setShowSheet] = React.useState(false);

    const { t } = useTranslation();
    if (feedInfo.totalCompleted === 0) {
      return null;
    }

    const userInfos = businessModule.completedUserInfo;

    return (
      <>
        <Separator />
        <YStack
          cursor={"pointer"}
          gap={"$small"}
          paddingHorizontal="$larger"
          $gtSm={{ paddingHorizontal: 0 }}
          onPress={() => {
            setShowSheet(true);
          }}
        >
          <Body muted>
            {t("business:completed_this_total", {
              count: feedInfo.totalCompleted,
            })}
            {feedInfo.totalSkipped > 0 &&
              " " +
                t("business:skipped_this_total", {
                  count: feedInfo.totalSkipped,
                })}
          </Body>
          <UserAvatarGroupRaw users={userInfos} size={AvatarSize.Medium} />
        </YStack>

        <BusinessModuleMembersSheet
          sheetId={"b-module-members"}
          open={showSheet}
          onOpenChange={setShowSheet}
          businessModule={businessModule}
        />
      </>
    );
  },
);

export const BusinessDoneStateUI = observer(
  ({ businessModule }: { businessModule: BusinessGenericModel }) => {
    const userUid = businessModule.userUid;
    const isCompleted = businessModule.isCompletedByUserOrTeam;
    const completedData = businessModule.isCompletedData;
    const wasSkipped = !!completedData?.skipped;
    const { t } = useTranslation();

    if (!userUid) {
      return null;
    }

    // Own Planira team found
    const plan = businessModule.businessPlan.planira;
    if (plan && plan.businessStaticUsers.length !== 1) {
      return (
        <PlanUsersCompletedList businessModule={businessModule} plan={plan} />
      );
    }

    let doneLabel: string = "";

    if (wasSkipped) {
      doneLabel = t("business:you_have_skipped_this");
    } else if (isCompleted) {
      doneLabel = t("business:you_have_completed_this");
    } else {
      doneLabel = t("business:you_set_as_completed");
    }

    return (
      <YStack gap={"$medium"}>
        {isCompleted && (
          <TipsText
            text={t("business:completed_already_info")}
            marginTop={"$medium"}
            paddingHorizontal="$larger"
            $gtSm={{ paddingHorizontal: 0 }}
          />
        )}
        <Button
          type={isCompleted ? "secondary" : "primary"}
          icon={isCompleted ? Undo2 : PartyPopper}
          onPress={() => {
            businessModule.toggleCompletedByUserId(userUid, false);
          }}
        >
          {doneLabel}
        </Button>

        {!isCompleted && (
          <PopoverConfirm
            type={"warning"}
            placement={"top"}
            confirmTitle={t("business:set_task_as_skipped_title")}
            confirmDescription={t("business:set_task_as_skipped_desc")}
            onConfirm={() => {
              // Store as skipped
              businessModule.toggleCompletedByUserId(userUid, true);
            }}
          >
            <Button type={"ghost"} iconPosition={"before"} icon={X}>
              {t("business:task_this_is_not_for_me")}
            </Button>
          </PopoverConfirm>
        )}

        <TotalCompletedInfo businessModule={businessModule} />
      </YStack>
    );
  },
);
